/**
 * Day 91a.5 P2D — Buy Score Weight Component tooltip fallback chain (R-Lock-2).
 *
 * Pure function for resolving header tooltip strings from bswc rows with an
 * optional hardcoded fallback. Used by BuyingSession.tsx column defs to wire
 * AG Grid `headerTooltip` per Q11 R4 category dispositions:
 *
 *   - Cat 2 (Sales/Mo with pre-existing hardcoded text): 4-step chain
 *     bswc.tooltip_text → bswc.label → hardcodedFallback → undefined.
 *     Admin override via Engine Editor → Weight Components → tooltip_text column populates bswc.tooltip_text;
 *     pre-existing hardcoded text preserved as 3rd fallback step for graceful
 *     degradation while admin curation is pending (NULL on all 25 rows
 *     post-Day-91a P1 substrate ship).
 *
 *   - Cat 3 (bare metric columns ROI%/ROI-Day/Profit-Unit/Profit-Day): 3-step
 *     chain bswc.tooltip_text → bswc.label → undefined. No hardcoded fallback;
 *     undefined return causes AG Grid to skip the tooltip entirely (V1 admin
 *     curation will populate bswc.tooltip_text or fall through to bswc.label).
 *
 *   - Cat 1 (tier-mode T${n} ROI%/PPU): EXEMPT. Tier headers KEEP existing
 *     tier-specific tooltip per R-Lock-3 (`Tier ${idx+1}: ${qty range} @ $price`);
 *     do NOT call resolveTooltip for tier-mode headers.
 *
 *   - Days/Unit: EXEMPT per R-Lock-6 A5(b) confirmed-not-bswc. No bswc.key
 *     match exists (colId=days_locked); hardcoded tooltip preserved.
 *
 * Pure function — no React; no Supabase; no side effects. Testable in Vitest
 * Block A per Day 89 #DBN-λ pure-function reclassify precedent + Day 91a.5
 * R-Lock-12 P3 cohort design. Day 91a P2B `src/lib/buyScoreRebalance.ts`
 * sibling pattern.
 *
 * Parameter naming: `bswcKey` (not `colKey` per Sol R-Lock-2) for semantic
 * accuracy — the lookup is against bswc.key column, not the AG Grid colId.
 * Caller maps grid column → bswc.key before invoking. CC P2D-time discretion
 * per Sol R2 §7 RE-LOCK pre-save note + Day 91a.5 R2 final lock.
 */

import type { BuyScoreWeightComponent } from '../types';

/**
 * Resolve the tooltip string for a Buy Score Weight Component grid header
 * via fallback chain. Returns undefined when no value resolves (caller should
 * pass result directly to AG Grid `headerTooltip` prop — undefined causes
 * AG Grid to skip the tooltip).
 *
 * @param bswcRows - bswc array (typically pre-filtered to is_available=true
 *                   per R-Lock-7 cascade; caller-side filter)
 * @param bswcKey  - the bswc.key value to look up (e.g., 'roi_pct',
 *                   'sales_velocity'); NOT the AG Grid colId
 * @param hardcodedFallback - optional 3rd-step fallback string (4-step chain).
 *                            Pass for columns with pre-existing hardcoded
 *                            tooltip text preserved as fallback (Q11 R4 cat 2);
 *                            omit for clean greenfield columns (Q11 R4 cat 3).
 * @returns string when any chain step resolves; undefined when all steps fail
 *
 * Chain semantic (R-Lock-2):
 *   step 1: bswc row's tooltip_text (admin-curated; NULL on all 25 rows
 *           post-Day-91a P1 until admin populates via Engine Editor → Weight Components dedicated edit route)
 *   step 2: bswc row's label (always non-null per DB constraint)
 *   step 3: hardcodedFallback (if provided)
 *   step 4: undefined (terminal; AG Grid skips tooltip)
 *
 * Edge cases:
 *   - bswcRows empty → falls to step 3 if hardcodedFallback provided, else 4
 *   - bswcKey not found in bswcRows → same as empty
 *   - bswc.tooltip_text empty string '' → treated as NULL per `||` semantic
 *     (falls through to label)
 */
export function resolveTooltip(
  bswcRows: BuyScoreWeightComponent[],
  bswcKey: string,
  hardcodedFallback?: string
): string | undefined {
  const row = bswcRows.find((r) => r.key === bswcKey);
  // Step 1: admin-curated tooltip text (most-specific)
  if (row?.tooltip_text) return row.tooltip_text;
  // Step 2: bswc label (always non-null per DB constraint when row exists)
  if (row?.label) return row.label;
  // Step 3: hardcoded fallback (if provided)
  if (hardcodedFallback) return hardcodedFallback;
  // Step 4: undefined — AG Grid skips tooltip
  return undefined;
}
